import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Megaphone,
    Calendar,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Heart,
    Sparkles
} from 'lucide-react';
import { getActiveAnnouncements, getAnnouncementImageUrl } from '../../services/announcement.service';

const categoryColors = {
    'mental-health': 'from-purple-600 to-indigo-600',
    'wellness': 'from-emerald-500 to-teal-600',
    'awareness': 'from-rose-500 to-pink-600',
    'workshop': 'from-blue-500 to-cyan-600',
    'seminar': 'from-amber-500 to-orange-600',
    'support-group': 'from-violet-500 to-purple-600',
    'other': 'from-gray-500 to-slate-600'
};

const categoryIcons = {
    'mental-health': Heart,
    'wellness': Sparkles,
    'awareness': Megaphone,
    'workshop': Calendar,
    'seminar': Calendar,
    'support-group': Heart,
    'other': Megaphone
};

const EventBanner = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isPaused, setIsPaused] = useState(false);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const response = await getActiveAnnouncements();
                if (response.status === 'success' && response.data?.length > 0) {
                    setAnnouncements(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch announcements:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAnnouncements();
    }, []);

    // Auto-rotate announcements
    useEffect(() => {
        if (announcements.length <= 1 || isPaused) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % announcements.length);
        }, 6000);

        return () => clearInterval(interval);
    }, [announcements.length, isPaused]);

    const goToNext = () => {
        setCurrentIndex((prev) => (prev + 1) % announcements.length);
    };

    const goToPrev = () => {
        setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
    };

    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const options = { month: 'short', day: 'numeric' };

        if (start.toDateString() === end.toDateString()) {
            return start.toLocaleDateString('en-US', { ...options, year: 'numeric' });
        }

        if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
            return `${start.toLocaleDateString('en-US', options)} - ${end.getDate()}, ${end.getFullYear()}`;
        }

        return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
    };

    if (loading || announcements.length === 0) {
        return null;
    }

    const currentAnnouncement = announcements[currentIndex];
    const CategoryIcon = categoryIcons[currentAnnouncement.category] || Megaphone;
    const gradientClass = categoryColors[currentAnnouncement.category] || categoryColors.other;
    const imageUrl = getAnnouncementImageUrl(currentAnnouncement);

    return (
        <section className="relative w-full">
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative overflow-hidden shadow-xl"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
                    {/* Background with gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${gradientClass} opacity-95`} />

                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                                    <circle cx="2" cy="2" r="1" fill="white" />
                                </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill="url(#dots)" />
                        </svg>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 max-w-7xl mx-auto w-full">
                        <div className="flex flex-col md:flex-row items-center gap-4 p-6 md:p-8 lg:px-12">
                        {/* Image (if available) */}
                        {imageUrl && (
                            <div className="flex-shrink-0 w-full md:w-48 h-32 md:h-36 rounded-xl overflow-hidden shadow-lg">
                                <img
                                    src={imageUrl}
                                    alt={currentAnnouncement.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}

                        {/* Text content */}
                        <div className="flex-1 text-center md:text-left">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentIndex}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {/* Category badge */}
                                    <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-semibold uppercase tracking-wider">
                                            <CategoryIcon className="w-3.5 h-3.5" />
                                            {currentAnnouncement.category.replace('-', ' ')}
                                        </span>
                                        {currentAnnouncement.priority === 'high' && (
                                            <span className="inline-flex items-center px-2 py-1 bg-yellow-400 text-yellow-900 rounded-full text-xs font-bold">
                                                Featured
                                            </span>
                                        )}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2 drop-shadow-md">
                                        {currentAnnouncement.title}
                                    </h3>

                                    {/* Description */}
                                    <p className="text-white/90 text-sm md:text-base mb-3 line-clamp-2 max-w-2xl">
                                        {currentAnnouncement.description}
                                    </p>

                                    {/* Date range */}
                                    <div className="flex items-center justify-center md:justify-start gap-2 text-white/80 text-sm mb-4">
                                        <Calendar className="w-4 h-4" />
                                        <span>{formatDateRange(currentAnnouncement.startDate, currentAnnouncement.endDate)}</span>
                                    </div>

                                    {/* CTA Button */}
                                    {currentAnnouncement.link && (
                                        <a
                                            href={currentAnnouncement.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                                        >
                                            Learn More
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Navigation controls */}
                        {announcements.length > 1 && (
                            <div className="flex md:flex-col items-center gap-2">
                                <button
                                    onClick={goToPrev}
                                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                                    aria-label="Previous announcement"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                {/* Dots indicator */}
                                <div className="flex md:flex-col gap-1.5 px-2">
                                    {announcements.map((_, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setCurrentIndex(idx)}
                                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                                idx === currentIndex
                                                    ? 'bg-white scale-125'
                                                    : 'bg-white/40 hover:bg-white/60'
                                            }`}
                                            aria-label={`Go to announcement ${idx + 1}`}
                                        />
                                    ))}
                                </div>

                                <button
                                    onClick={goToNext}
                                    className="p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
                                    aria-label="Next announcement"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                        </div>
                    </div>
                </motion.div>
        </section>
    );
};

export default EventBanner;
